import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import type { SortingState } from '@tanstack/react-table';
import { snakeCase } from 'lodash-es';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  InlineLoading,
  InlineNotification,
  Modal,
  Tag,
} from '@carbon/react';
import { Link as LinkIcon, Unlink } from '@carbon/react/icons';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { pagedCsvExport } from 'components/data-table/fetch-all-pages';
import { Date as FormattedDate } from 'components/date';
import { DocumentCatalog } from 'components/vesting-documents/document-catalog';
import { EMPTY, recordingReference } from 'components/vesting-documents/types';
import { col } from 'components/vesting-documents/citation-columns';
import { entityPath } from 'components/related-items/record-kind-configs';
import { StatsBar, type StatItem } from 'components/stats-bar';
import { WorkspaceCard, WorkspaceCardNote } from 'components/workspace-cards';
import {
  readWriteFailure,
  UNEXPLAINED_FAILURE,
} from 'pages/well-workspaces/utils';
import { US_DATETIME_FORMAT } from 'utils/dates';
import { DeltaIndicator } from 'pages/interest-certification/workspaces/show/comparison-table/delta-indicator';
import { format8 } from 'utils/number';
import {
  deleteManualLink,
  fetchPaged,
  keys,
  postComparisonRun,
  pagedLoading,
  postManualLink,
  useComparisonRun,
  useKeyedPaging,
  usePaged,
  useWorkspaceWrite,
  type PagedOptions,
  type PagedResource,
  type PagedRows,
} from '../api';
import { LinkList } from '../party-links';
import type {
  ComparisonKind,
  ComparisonRun,
  MatchStatus,
  OwnershipComparisonRow,
  OwnershipRun,
  OwnershipSummary,
  RunsheetRow,
  RunsheetRun,
  WorkspaceEnvelope,
} from '../types';
import {
  MATCH_LABELS,
  NO_ROWS,
  VERDICT_LABELS,
  PIN_ACTIONS,
  RUNSHEET_COLUMNS,
  RowAction,
  actionsCol,
  anySelected,
  dateCol,
  linkOr,
  notInWorkspace,
  workbookCols,
  tractHref,
  type JumpToCard,
} from './columns';
import {
  OWNERSHIP_FLAGS,
  ownershipFlags,
  ownershipTract,
  toColumnFilters,
  toParams,
  useReportRows,
  type ReportRows,
  type TableFilters,
} from './filters';
import styles from './page.module.scss';

const MATCH_TAGS: Record<MatchStatus, StatItem['tagType']> = {
  matched: 'green',
  manual: 'blue',
  ambiguous: 'purple',
  absent: 'red',
  unmatched: 'gray',
};

const OWNERSHIP_STATS: [keyof OwnershipSummary, string, StatItem['tagType']][] =
  [
    ['match', 'Match', 'green'],
    ['valueMismatch', 'Value mismatch', 'red'],
    ['missingInCounterparty', 'Missing in CRM', 'magenta'],
    ['notComparable', 'Not comparable', 'gray'],
    ['entityUnlinked', 'Entity unlinked', 'gray'],
    ['unresolvedTract', 'Unresolved tract', 'gray'],
    ['outsideWorkspace', 'Not in workspace', 'gray'],
    ['comparedRows', 'Compared rows', 'cool-gray'],
  ];

// TitleSupportWorkspace::Page::EXPORT_MAX_PAGE_SIZE: one request, as each filters and sorts every row on the server.
const EXPORT_PAGE_SIZE = 10_000;

const EXPORT_FILENAMES: Partial<Record<PagedResource, string>> = {
  standard_mor: 'ownership',
};

// TanStack takes a column's first sort direction from the loaded first row, which a server sort reorders.
const ascFirst = <Row,>(columns: DataTableColumnDef<Row>[]) =>
  columns.map((column) => ({ ...column, sortDescFirst: false }));

// Spread tableProps onto the DataTable: it renders and exports the same columns.
export const usePagedTable = <Resource extends PagedResource>(
  id: string,
  resource: Resource,
  filters: TableFilters,
  columns: DataTableColumnDef<PagedRows[Resource]>[],
  // A locked comparison shows no rows, so it asks for none.
  enabled = true
) => {
  const { page, pageSize, firstPage, onPaginationChange } = useKeyedPaging(
    JSON.stringify(filters)
  );
  // The server searches every shown column, so the search box reaches all pages.
  const [text, setText] = useState('');
  // Sorted on the server too, across every page.
  const [sorting, setSorting] = useState<SortingState>([]);
  const [sort] = sorting;
  const params = toParams(filters);
  const order: PagedOptions = sort
    ? { sort: snakeCase(sort.id), direction: sort.desc ? 'desc' : 'asc' }
    : {};
  const query = usePaged(
    id,
    resource,
    page,
    pageSize,
    params,
    text,
    order,
    enabled
  );
  const tableColumns = useMemo(() => ascFirst(columns), [columns]);
  return {
    rows: query.data?.results ?? NO_ROWS,
    facets: query.data?.facets,
    tableProps: {
      columns: tableColumns,
      isLoading: pagedLoading(query),
      error: readWriteFailure(query.error, UNEXPLAINED_FAILURE),
      config: {
        columnFilters: false,
        sizeToContent: true,
        export: pagedCsvExport(
          EXPORT_FILENAMES[resource] ?? resource,
          (page, pageSize, signal) =>
            fetchPaged(
              id,
              resource,
              page,
              pageSize,
              params,
              text,
              { ...order, export: true },
              signal
            ),
          EXPORT_PAGE_SIZE
        ),
      },
      controlledState: { pagination: { page, pageSize }, sorting },
      // A sort or search starts again at page 1, in the same render as the change.
      onSortChange: (state: { sorting: SortingState }) => {
        setSorting(state.sorting);
        firstPage();
      },
      scroll: {
        mode: 'paginated' as const,
        serverDriven: true,
        totalCount: query.data?.totalCount ?? 0,
      },
      onPaginationChange,
      onGlobalSearchChange: (state: { globalFilter: string }) => {
        setText(state.globalFilter?.trim() ?? '');
        firstPage();
      },
    },
  };
};

// A comparison computes when its card mounts unless the workspace already holds a
// current run, which is read back instead. A promoted workspace only ever reads.
const useRunner = <Run extends ComparisonRun>(
  id: string,
  kind: ComparisonKind,
  detail: WorkspaceEnvelope,
  locked: boolean
) => {
  const queryClient = useQueryClient();
  const existing = detail.titleSupport.runs[kind];
  const stale = !locked && !detail.promoted && (!existing || existing.stale);
  const compute = useMutation({
    mutationFn: () => postComparisonRun<Run>(id, kind),
    retry: false,
    onSuccess: (run) => {
      queryClient.setQueryData(keys.run(id, run.id), run);
      queryClient.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
  const read = useComparisonRun<Run>(
    id,
    locked || stale || compute.data ? null : (existing?.id ?? null)
  );
  // Once per stale run, keyed by the run it replaces, so a failing compute is not retried in a loop.
  const startedFor = useRef<number | null>();
  const existingId = existing?.id ?? null;
  const { mutate } = compute;
  useEffect(() => {
    if (!stale || startedFor.current === existingId) return;
    startedFor.current = existingId;
    mutate();
  }, [stale, existingId, mutate]);
  const run = compute.data ?? read.data;
  return {
    run,
    completed: run?.status === 'completed' ? run : null,
    ranAt: run?.ranAt ?? existing?.ranAt,
    busy: compute.isPending || read.isLoading,
    error: compute.error ?? (run?.status === 'failed' ? run.error : null),
    rerun: detail.promoted ? undefined : () => mutate(),
  };
};

export const LOCKED_EXPLANATION =
  'Every tract needs a converted closing-report workbook in Title Support to run this comparison. Use Link Item to add one.';

const RunCard: FC<{
  title: string;
  runner: ReturnType<typeof useRunner>;
  locked: boolean;
  children?: ReactNode;
}> = ({ title, runner: { busy, error, ranAt, rerun }, locked, children }) => (
  <WorkspaceCard title={title}>
    <div className={styles.section}>
      {locked && <WorkspaceCardNote>{LOCKED_EXPLANATION}</WorkspaceCardNote>}
      {!locked && ranAt && (
        <p className={styles.context}>
          Last run <FormattedDate date={ranAt} format={US_DATETIME_FORMAT} />
        </p>
      )}
      {busy && <InlineLoading description="Comparing…" />}
      {error ? (
        <div className={styles.actions}>
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title="Comparison failed"
            subtitle={
              typeof error === 'string'
                ? error
                : (readWriteFailure(error, UNEXPLAINED_FAILURE) ?? '')
            }
          />
          {rerun && !busy && (
            <Button kind="tertiary" size="sm" onClick={rerun}>
              Run again
            </Button>
          )}
        </div>
      ) : null}
      {locked ? null : children}
    </div>
  </WorkspaceCard>
);

// StatsBar reads a count missing from an older stored run as none.
const rateStat = (label: string, rate: number | undefined): StatItem => ({
  label,
  value: Math.round((rate ?? 0) * 100),
  suffix: '%',
  tagType: 'teal',
});

const valueCol = (
  id: 'reportValue' | 'crmValue' | 'crmTractValue',
  header: string,
  size: number
): DataTableColumnDef<OwnershipComparisonRow> => ({
  id,
  header,
  size,
  cell: ({ row }) => format8(row.original[id], EMPTY),
});

interface ComparisonProps {
  id: string;
  detail: WorkspaceEnvelope;
  locked: boolean;
  filters: TableFilters;
  onRows: ReportRows;
}

// The row's citation, as the one-line summary above the picker.
const rowSummary = (row: RunsheetRow) =>
  [
    row.documentType,
    recordingReference(row.volume, row.page, row.instrumentNumber),
    row.countyName,
    row.recordedDate,
  ]
    .filter(Boolean)
    .join(' · ');

// Linking a runsheet row uses the same document picker as a vesting document, seeded with the row's citation.
const LinkRowModal: FC<{
  row: RunsheetRow;
  onClose: () => void;
  onLink: (documentId: number) => void;
}> = ({ row, onClose, onLink }) => {
  const [selected, setSelected] = useState<number[]>([]);
  const seed = [row.volume, row.page].every(Boolean)
    ? `${row.volume} ${row.page}`
    : (row.instrumentNumber ?? '');
  return (
    <Modal
      open
      size="lg"
      modalHeading="Link document"
      className={styles.tearsheet}
      primaryButtonText="Link"
      secondaryButtonText="Cancel"
      primaryButtonDisabled={selected.length === 0}
      onRequestSubmit={() => onLink(selected[0])}
      onRequestClose={onClose}
      hasScrollingContent
    >
      <WorkspaceCardNote>{rowSummary(row)}</WorkspaceCardNote>
      <DocumentCatalog
        initialText={seed}
        selectedDocumentIds={selected}
        onSelect={setSelected}
      />
    </Modal>
  );
};

export const RunsheetComparison: FC<ComparisonProps> = ({
  id,
  detail,
  locked,
  filters,
  onRows,
}) => {
  const runner = useRunner<RunsheetRun>(id, 'runsheet', detail, locked);
  const [linkingRow, setLinkingRow] = useState<RunsheetRow | null>(null);
  const link = useWorkspaceWrite(
    ({ row, documentId }: { row: RunsheetRow; documentId: number }) =>
      postManualLink(id, row.id, documentId)
  );
  const unlink = useWorkspaceWrite((row: RunsheetRow) =>
    deleteManualLink(id, row.id)
  );
  const { mutate: unlinkMutate, isPending: unlinking } = unlink;
  const linkError = link.error ?? unlink.error;

  const tractContext = detail.titleSupport.tracts
    .map(
      (tract) =>
        `${tract.name} (${tract.countyName ?? EMPTY}, ${
          tract.referenceLocationName ?? EMPTY
        })`
    )
    .join('; ');
  const summary = runner.completed?.summary;

  const columns = useMemo<DataTableColumnDef<RunsheetRow>[]>(
    () => [
      {
        ...col<RunsheetRow>('matchStatus', 'Match Status', 120, {
          type: 'enum',
          getExportValue: (row) => {
            const { matchStatus, tractInWorkspace } = row as RunsheetRow;
            return notInWorkspace(MATCH_LABELS[matchStatus], tractInWorkspace);
          },
        }),
        cell: ({ row }) => (
          <span className={styles.pills}>
            <Tag type={MATCH_TAGS[row.original.matchStatus]} size="sm">
              {MATCH_LABELS[row.original.matchStatus]}
            </Tag>
            {row.original.pending && (
              <Tag type="teal" size="sm">
                Pending
              </Tag>
            )}
            {row.original.tractInWorkspace === false && (
              <>
                <Tag type="gray" size="sm">
                  Not in workspace
                </Tag>
                <LinkList
                  links={(row.original.sheetTracts ?? []).map((tract) => ({
                    key: tract.salesforceTractId,
                    to: tractHref(tract.salesforceTractId),
                    label: tract.name,
                  }))}
                />
              </>
            )}
          </span>
        ),
      },
      {
        id: 'document',
        accessorFn: (row) => row.document?.id,
        header: 'Document',
        size: 110,
        enableSorting: false,
        meta: { getExportHref: (row) => (row as RunsheetRow).document?.href },
        cell: ({ row }) => {
          const doc = row.original.document;
          return doc ? linkOr(String(doc.id), doc.href) : EMPTY;
        },
      },
      dateCol<RunsheetRow>(
        'insightsTransactionDate',
        'Insights Transaction Date',
        220
      ),
      ...RUNSHEET_COLUMNS,
      actionsCol<RunsheetRow>(100, ({ row }) => {
        // Another tract's tab is read-only here: a hand link skips it.
        if (row.original.tractInWorkspace === false) return null;
        return (
          <div className={styles.rowActions}>
            <RowAction
              renderIcon={LinkIcon}
              iconDescription={
                row.original.document ? 'Change document' : 'Link document'
              }
              disabled={detail.promoted}
              onClick={() => setLinkingRow(row.original)}
            />
            {row.original.matchStatus === 'manual' && (
              <RowAction
                renderIcon={Unlink}
                iconDescription="Unlink document"
                disabled={detail.promoted || unlinking}
                onClick={() => unlinkMutate(row.original)}
              />
            )}
          </div>
        );
      }),
    ],
    [detail.promoted, unlinkMutate, unlinking]
  );
  const runsheet = usePagedTable(id, 'runsheet', filters, columns, !locked);
  useReportRows('runsheet', onRows, runsheet.rows, runsheet.facets);

  return (
    <RunCard title="Runsheet Comparison" runner={runner} locked={locked}>
      <p className={styles.context}>
        {`Searching all documents. Tracts: ${tractContext}`}
      </p>
      {summary && (
        <StatsBar
          stats={[
            ...(Object.keys(MATCH_TAGS) as MatchStatus[]).map((status) => ({
              label: MATCH_LABELS[status],
              value: summary[status],
              tagType: MATCH_TAGS[status],
            })),
            { label: 'Total', value: summary.total, tagType: 'cool-gray' },
            {
              label: 'Not in workspace',
              value: summary.outsideWorkspace ?? 0,
              tagType: 'gray',
            },
            rateStat('Coverage', summary.coverage),
          ]}
        />
      )}
      {linkError ? (
        <InlineNotification
          kind="error"
          lowContrast
          title="Error"
          subtitle={
            readWriteFailure(
              linkError,
              `Could not change the link. ${UNEXPLAINED_FAILURE}`
            ) ?? undefined
          }
          onCloseButtonClick={() => {
            link.reset();
            unlink.reset();
          }}
        />
      ) : null}
      <DataTable<RunsheetRow>
        data={runsheet.rows}
        getRowId={(row) => String(row.id)}
        initialTableState={PIN_ACTIONS}
        {...runsheet.tableProps}
      />
      {linkingRow && (
        <LinkRowModal
          row={linkingRow}
          onClose={() => setLinkingRow(null)}
          onLink={(documentId) => {
            link.mutate({ row: linkingRow, documentId });
            setLinkingRow(null);
          }}
        />
      )}
    </RunCard>
  );
};

export const OwnershipComparison: FC<
  ComparisonProps & { onJump: JumpToCard }
> = ({ id, detail, locked, onJump, filters, onRows }) => {
  const runner = useRunner<OwnershipRun>(id, 'ownership', detail, locked);
  const columnFilters = useMemo(() => toColumnFilters(filters), [filters]);
  const summary = runner.completed?.summary;
  const rows = runner.run?.rows ?? NO_ROWS;
  useReportRows('ownership', onRows, rows);

  const columns = useMemo<DataTableColumnDef<OwnershipComparisonRow>[]>(
    () => [
      {
        ...col<OwnershipComparisonRow>('entityName', 'Entity', 320, {
          type: 'enum',
        }),
        cell: ({ row }) => {
          const { entityId, entityName } = row.original;
          return linkOr(entityName, entityPath(entityId), entityName);
        },
      },
      {
        id: 'tract',
        accessorFn: ownershipTract,
        header: 'Tract',
        size: 140,
        meta: { type: 'enum' },
        cell: ({ row }) => {
          const { salesforceTractId } = row.original;
          return linkOr(
            ownershipTract(row.original),
            salesforceTractId && tractHref(salesforceTractId),
            salesforceTractId
          );
        },
      },
      col('stick', 'Stick', 130, { type: 'enum' }),
      valueCol('reportValue', 'Report Value', 130),
      valueCol('crmValue', 'CRM Value', 130),
      {
        id: 'delta',
        header: 'Delta',
        size: 200,
        cell: ({ row }) => {
          const { reportValue, crmValue } = row.original;
          const both =
            typeof reportValue === 'number' && typeof crmValue === 'number';
          return (
            <DeltaIndicator
              diff={both ? reportValue - crmValue : null}
              base={both ? crmValue : null}
            />
          );
        },
      },
      valueCol('crmTractValue', 'CRM Row', 150),
      {
        ...col<OwnershipComparisonRow>('verdict', 'Verdict', 150, {
          type: 'enum',
        }),
        cell: ({ row }) => (
          <Tag
            type={row.original.verdict === 'match' ? 'green' : 'red'}
            size="sm"
          >
            {VERDICT_LABELS[row.original.verdict]}
          </Tag>
        ),
      },
      // Sorted here in the browser, unlike the paged tables.
      ...workbookCols<OwnershipComparisonRow>(onJump).map((column) => ({
        ...column,
        enableSorting: true,
      })),
      {
        id: 'flags',
        accessorFn: ownershipFlags,
        header: 'Flags',
        size: 260,
        meta: { type: 'enum', wrap: true },
        filterFn: (row, _columnId, selected: string[]) =>
          anySelected(ownershipFlags(row.original), selected),
        cell: ({ row }) => {
          const flags = ownershipFlags(row.original);
          if (!flags.length) return EMPTY;
          return (
            <div className={styles.rowActions}>
              {flags.map((flag) => (
                <Tag
                  key={flag}
                  type={flag === 'not_in_workspace' ? 'gray' : 'magenta'}
                  size="sm"
                >
                  {OWNERSHIP_FLAGS[flag]}
                </Tag>
              ))}
            </div>
          );
        },
      },
    ],
    [onJump]
  );

  return (
    <RunCard title="Ownership Comparison" runner={runner} locked={locked}>
      {summary && (
        <StatsBar
          stats={[
            ...OWNERSHIP_STATS.map(([key, label, tagType]) => ({
              label,
              value: summary[key],
              tagType,
            })),
            rateStat('Match rate', summary.matchRate),
          ]}
        />
      )}
      <DataTable<OwnershipComparisonRow>
        data={rows}
        columns={columns}
        getRowId={(row, index) => `${row.entityName}-${row.stick}-${index}`}
        isLoading={runner.busy}
        config={{ columnFilters: false, sizeToContent: true, export: true }}
        controlledState={{ columnFilters }}
      />
    </RunCard>
  );
};
